/**
 * Persistent navigation component shown across all protected pages.
 * Always displays: Capabilities / Postings / Resumes [/ breadcrumb]
 * The current page is shown as text, others as clickable links.
 *
 * Pass `breadcrumb` to append an extra level (e.g. a resume title on /resumes/:id).
 */
import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut, Coins } from 'lucide-react';
import { authApi, creditsApi } from '@/lib/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

interface NavigationProps {
  /** Optional label shown as a 4th breadcrumb item after "Resumes" */
  breadcrumb?: string;
}

export function Navigation({ breadcrumb }: NavigationProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const { data: authData } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: authApi.me,
    retry: false,
  });

  const { data: plan } = useQuery({
    queryKey: ['credits', 'balance'],
    queryFn: creditsApi.getBalance,
    staleTime: 10_000, // 10 seconds
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => { queryClient.clear(); navigate('/login'); },
  });

  const isCapabilities = location.pathname === '/capabilities';
  const isPostings = location.pathname === '/postings';
  // Exact match: only the list page is "current"; sub-routes keep Resumes as a link.
  const isResumes = location.pathname === '/resumes';
  const isApplications = location.pathname === '/applications';

  return (
    <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
        <nav className="flex items-center gap-4 text-sm">
          {!isCapabilities ? (
            <button
              onClick={() => navigate('/capabilities')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Capabilities
            </button>
          ) : (
            <span className="font-medium">Capabilities</span>
          )}

          <span className="text-muted-foreground">/</span>

          {!isPostings ? (
            <button
              onClick={() => navigate('/postings')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Postings
            </button>
          ) : (
            <span className="font-medium">Postings</span>
          )}

          <span className="text-muted-foreground">/</span>

          {!isResumes ? (
            <button
              onClick={() => navigate('/resumes')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Resumes
            </button>
          ) : (
            <span className="font-medium">Resumes</span>
          )}

          <span className="text-muted-foreground">/</span>

          {!isApplications ? (
            <button
              onClick={() => navigate('/applications')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              Applications
            </button>
          ) : (
            <span className="font-medium">Applications</span>
          )}

          {/* Admin link — visible only for admins */}
          {authData?.user?.isAdmin && (
            <>
              <span className="text-muted-foreground">/</span>
              <button
                onClick={() => navigate('/admin')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Admin
              </button>
            </>
          )}

          {/* Optional extra breadcrumb level — e.g. the name of the open resume */}
          {breadcrumb && (
            <>
              <span className="text-muted-foreground">/</span>
              <span className="font-medium truncate max-w-xs">{breadcrumb}</span>
            </>
          )}
        </nav>

        <div className="flex items-center gap-4">
          {plan && (
            <div
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium transition-colors ${
                plan.creditsBalance === 0
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-gray-200 bg-gray-50 text-gray-700'
              }`}
            >
              <Coins className="h-3 w-3" />
              {plan.creditsBalance} credits
            </div>
          )}

          <button
            onClick={() => logoutMutation.mutate()}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      </div>
    </header>
  );
}
